import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Notification } from '../../types/notification';

interface NotificationCenterProps {
  notifications: Notification[];
  onMarkAsRead: (id: string) => void;
  onMarkAllAsRead: () => void;
  onClose: () => void;
}

function getTimeAgo(date: Date): string {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

function getTypeIcon(type: Notification['type']): string {
  switch (type) {
    case 'card_view': return '👁️';
    case 'new_contact': return '🤝';
    case 'feature_update': return '✨';
    case 'seasonal_reminder': return '🔄';
    case 're_engagement': return '👋';
    case 'milestone': return '🎉';
    case 'system': return '📢';
    default: return '🔔';
  }
}

const NotificationCenter: React.FC<NotificationCenterProps> = ({
  notifications,
  onMarkAsRead,
  onMarkAllAsRead,
  onClose,
}) => {
  const navigate = useNavigate();
  const unreadCount = notifications.filter((n) => !n.read).length;

  const handleNotificationClick = (notification: Notification) => {
    if (!notification.read) {
      onMarkAsRead(notification.id);
    }
    if (notification.actionUrl) {
      navigate(notification.actionUrl);
      onClose();
    }
  };

  return (
    <div
      className="absolute right-0 top-full mt-2 w-80 sm:w-96 bg-dark-card border border-dark-border rounded-xl shadow-dark-xl z-50 overflow-hidden"
      role="dialog"
      aria-label="Notifications"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-dark-border">
        <h3 className="text-sm font-semibold text-dark-text font-display">
          Notifications
          {unreadCount > 0 && (
            <span className="ml-2 inline-flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-[#3B82F6] rounded-full">
              {unreadCount}
            </span>
          )}
        </h3>
        <div className="flex items-center gap-2">
          {unreadCount > 0 && (
            <button
              onClick={onMarkAllAsRead}
              className="text-xs text-[#3B82F6] hover:text-blue-400 bg-transparent border-none cursor-pointer transition-colors"
              aria-label="Mark all notifications as read"
            >
              Mark all read
            </button>
          )}
          <button
            onClick={onClose}
            className="text-dark-text-secondary hover:text-dark-text bg-transparent border-none cursor-pointer p-1 transition-colors"
            aria-label="Close notifications"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      {/* Notification List */}
      <div className="max-h-96 overflow-y-auto" role="list" aria-label="Notification list">
        {notifications.length === 0 ? (
          <div className="px-4 py-8 text-center">
            <p className="text-dark-text-secondary text-sm">No notifications yet</p>
          </div>
        ) : (
          notifications.map((notification) => (
            <button
              key={notification.id}
              onClick={() => handleNotificationClick(notification)}
              className={`w-full text-left px-4 py-3 border-b border-dark-border/50 cursor-pointer transition-colors bg-transparent border-x-0 border-t-0 ${
                notification.read
                  ? 'bg-dark-card hover:bg-dark-bg'
                  : 'bg-dark-bg hover:bg-dark-card'
              }`}
              role="listitem"
              aria-label={`${notification.read ? '' : 'Unread: '}${notification.title}`}
            >
              <div className="flex gap-3">
                <span className="text-lg flex-shrink-0 mt-0.5" aria-hidden="true">
                  {getTypeIcon(notification.type)}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <p className={`text-sm font-medium truncate ${
                      notification.read ? 'text-dark-text-secondary' : 'text-dark-text'
                    }`}>
                      {notification.title}
                    </p>
                    {!notification.read && (
                      <span className="w-2 h-2 rounded-full bg-[#3B82F6] flex-shrink-0 mt-1.5" aria-hidden="true" />
                    )}
                  </div>
                  <p className="text-xs text-dark-text-secondary mt-0.5 line-clamp-2">
                    {notification.message}
                  </p>
                  <div className="flex items-center justify-between mt-1.5">
                    <span className="text-xs text-dark-text-secondary/70">
                      {getTimeAgo(notification.createdAt)}
                    </span>
                    {notification.actionLabel && (
                      <span className="text-xs text-[#3B82F6] font-medium">
                        {notification.actionLabel}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </button>
          ))
        )}
      </div>

      {/* Footer */}
      {notifications.length > 0 && (
        <div className="px-4 py-2 border-t border-dark-border text-center">
          <button
            onClick={() => { navigate('/notifications'); onClose(); }}
            className="text-xs text-[#3B82F6] hover:text-blue-400 bg-transparent border-none cursor-pointer transition-colors"
          >
            View all notifications
          </button>
        </div>
      )}
    </div>
  );
};

export default NotificationCenter;
